import { QueryKey, useMutation, useQuery, UseQueryOptions } from 'react-query'
import Swal from 'sweetalert2'

import { SpeakerBlacklistCheckStatus } from '@/src/components/VoiceCloningForm/hooks/useSpeakerBlacklistCheck'
import { navigateSafely } from '@/src/utils/safeNavigation'

import type { HubVoiceStatus, Tags, Voice, VoiceDropdownItem } from '../components/NewHub/Narrator/VoiceDropDown/types'
import { VoiceCloneType } from '../components/VoiceCloningForm/state'
import { CSRF } from '../utils/CSRF'
import { ObjectUtils } from '../utils/ObjectUtils'
import { API_BASE_URL, BaseRepository } from './BaseRepository'

export interface VoiceChild {
  name: string
  uuid: string
  status: Resemble.VoiceStatus
}

export interface NewVoiceInfo {
  DEFAULT_VOICE_NAME: string
  CAN_CREATE_PROFESSIONAL_VOICE: boolean
  PROFESSIONAL_VOICE_LIMIT: number
  CAN_CREATE_RAPID_VOICE: boolean
  RAPID_VOICE_LIMIT: number
  RAPID_VOICE_COUNT: number
  PROFESSIONAL_VOICE_COUNT: number
  DEFAULT_RAPID_VOICE_NAME: string
  DEFAULT_PROFESSIONAL_VOICE_NAME: string
}

export type VoiceChildData = VoiceChild & { created_at: string; updated_at: string; dataset_url: string; callback_url: string }

export interface VoiceSettingsPreset {
  uuid: string
  name: string
  settings: {
    pace?: number
    pitch?: number
    temperature?: number
    exaggeration?: number
    description?: string
    useHd?: boolean
  }
  is_public: boolean
  created_at: string
  updated_at: string
}

export interface PublicVoiceModel {
  id: number
  name: string
  label: string
}

export type AllVoicesData = {
  custom_voices: VoiceDropdownItem[]
  ai_voices: VoiceDropdownItem[]
  recent_voices: VoiceDropdownItem[]
  pinned_voices: VoiceDropdownItem[]
  public_voice_models?: PublicVoiceModel[]
  voice_settings_presets?: {
    user_presets: VoiceSettingsPreset[]
    default_presets: VoiceSettingsPreset[]
  }
}

interface UpdateVoiceDetailsParams {
  name: string
  filters: Partial<Tags>
  description: string
}

export class VoiceRepository extends BaseRepository {
  getPaginatedVoices = async (page: number = 1) => {
    return (
      await super.getHttpClientInstance().get<{
        voices: Voice[]
        pagination: {
          current_page: number
          total_pages: number
          total_results: number
          per_page: number
        }
        user_info: {
          can_upload: boolean
          is_over_limit: boolean
          voice_count: number
        }
      }>(`/voices.json?page=${page}`, {
        'X-CSRF-Token': CSRF.getToken(),
        'Content-Type': 'application/json',
      })
    ).data
  }

  getUninitializedVoices = async () => {
    return (
      await super.getHttpClientInstance().get<VoiceChildData[]>('/voices/uninitialized', {
        'X-CSRF-Token': CSRF.getToken(),
        'Content-Type': 'application/json',
      })
    ).data
  }

  getPreviewVoice = async () => {
    return (
      await super.getHttpClientInstance().get<{
        preview_voice: Voice
      }>('/voices/voice_preview', {
        'X-CSRF-Token': CSRF.getToken(),
        'Content-Type': 'application/json',
      })
    ).data
  }

  getNewVoiceInfo = async () => {
    return (
      await super.getHttpClientInstance().get<NewVoiceInfo & { success: boolean }>('/voices/new_voice_info', {
        'X-CSRF-Token': CSRF.getToken(),
        'Content-Type': 'application/json',
      })
    ).data
  }

  createRapidOrProVoice = async (formData: FormData) => {
    return (
      await super
        .getHttpClientInstance()
        .configure(API_BASE_URL, 60 * 60 * 1000)
        .post<{ voice_uuid: string }>('/voices/create_voice', formData, {
          'X-CSRF-Token': CSRF.getToken(),
          'Content-Type': 'multipart/form-data',
        })
    ).data
  }

  createVoice = async (formData: any) => {
    return (
      await super.getHttpClientInstance().post('/voices.json', formData, {
        'X-CSRF-Token': CSRF.getToken(),
        'Content-Type': 'multipart/form-data',
      })
    ).data
  }

  getCustomVoices = async () => {
    return (
      await super.getHttpClientInstance().get('/api/v2/voices/custom.json', {
        'X-CSRF-Token': CSRF.getToken(),
        'Content-Type': 'application/json',
      })
    ).data
  }

  getAllVoices = async () => {
    return (
      await super.getHttpClientInstance().get<AllVoicesData>('/api/v2/voices/all', {
        'X-CSRF-Token': CSRF.getToken(),
        'Content-Type': 'application/json',
      })
    ).data
  }

  buildVoiceOnDemand = async (voiceUuid: string, intent: 'exit_build' | 'continue_record') => {
    const data = {
      intent,
    }
    return (
      await super.getHttpClientInstance().put(`/voices/${voiceUuid}/build_voice_on_demand`, ObjectUtils.convertObjToFormData(data), {
        'X-CSRF-Token': CSRF.getToken(),
        'Content-Type': 'application/json',
      })
    ).data
  }

  createConsent = async (voiceType: VoiceCloneType, language: string, consentAudio?: File | null) => {
    const obj = {
      voice_type: voiceType,
      language: language,
      ...(consentAudio ? { consent_audio: consentAudio } : {}),
    }
    const fd = ObjectUtils.convertObjToFormData(obj)
    return (
      await super
        .getHttpClientInstance()
        .configure(API_BASE_URL, 60 * 60 * 1000) // 1 hour
        .post<{
          consent_uuid: string
        }>('/voices/validate_consent', fd, {
          'X-CSRF-Token': CSRF.getToken(),
        })
    ).data
  }

  getConsentStatus = async (consentUuid: string) => {
    return (
      await super.getHttpClientInstance().get<{
        status: 'success' | 'processing' | 'failed'
        message: string | null
      }>(`/voices/consent_status?consent_uuid=${consentUuid}`, {
        'X-CSRF-Token': CSRF.getToken(),
        'Content-Type': 'application/json',
      })
    ).data
  }

  getSpeakerSimilarityStatus = async (consentUuid: string) => {
    return (
      await super.getHttpClientInstance().get<{
        status: 'success' | 'processing' | 'failed'
        message: string | null
      }>(`/voices/speaker_similarity_status?consent_uuid=${consentUuid}`, {
        'X-CSRF-Token': CSRF.getToken(),
        'Content-Type': 'application/json',
      })
    ).data
  }

  getVoiceStatus = async (voiceUuid: string) => {
    return (
      await super.getHttpClientInstance().get<{
        status: HubVoiceStatus
      }>(`/voices/voice_status?voice_uuid=${voiceUuid}`, {
        'X-CSRF-Token': CSRF.getToken(),
        'Content-Type': 'application/json',
      })
    ).data
  }

  getVoice = async (voiceUuid: string) => {
    return (
      await super.getHttpClientInstance().get<Resemble.Voice>(`/voices/${voiceUuid}.json`, {
        'X-CSRF-Token': CSRF.getToken(),
        'Content-Type': 'application/json',
      })
    ).data
  }

  updateVoice = async (voiceUuid: string, voicePatch: Partial<Resemble.Voice>) => {
    return await super.getHttpClientInstance().patch(`/voices/${voiceUuid}.json`, voicePatch, {
      'X-CSRF-Token': CSRF.getToken(),
      'Content-Type': 'application/json',
    })
  }

  deleteVoice = async (voiceUuid: string) => {
    return (
      await super.getHttpClientInstance().delete(`/api/v2/voices/${voiceUuid}`, {
        'X-CSRF-Token': CSRF.getToken(),
        'Content-Type': 'application/json',
      })
    ).data
  }

  upgradeVoice = async (voiceUuid: string) => {
    return (
      await super.getHttpClientInstance().post(
        `/api/v2/voices/${voiceUuid}/upgrade`,
        {},
        {
          'X-CSRF-Token': CSRF.getToken(),
          'Content-Type': 'application/json',
        },
      )
    ).data
  }

  pinVoice = async (voiceUuid: string) => {
    return (
      await super.getHttpClientInstance().post(
        `/api/v2/voices/${voiceUuid}/pin`,
        {},
        {
          'X-CSRF-Token': CSRF.getToken(),
          'Content-Type': 'application/json',
        },
      )
    ).data
  }

  unpinVoice = async (voiceUuid: string) => {
    return (
      await super.getHttpClientInstance().delete(`/api/v2/voices/${voiceUuid}/pin`, {
        'X-CSRF-Token': CSRF.getToken(),
        'Content-Type': 'application/json',
      })
    ).data
  }

  getVoiceChildren(voiceUuid: string) {
    return super.getHttpClientInstance().get<VoiceChild[]>(`/voices/${voiceUuid}/children`, {
      'X-CSRF-Token': CSRF.getToken(),
      'Content-Type': 'application/json',
    })
  }

  getMarketplaceVoiceChildren(voiceUuid: string) {
    return super.getHttpClientInstance().get<VoiceChild[]>(`/voices/${voiceUuid}/marketplace_voice_children`, {
      'X-CSRF-Token': CSRF.getToken(),
      'Content-Type': 'application/json',
    })
  }

  useVoiceChildren = (voiceUuid: string) =>
    useQuery<VoiceChild[]>(`${voiceUuid}`, async () => {
      return (await this.getVoiceChildren(voiceUuid)).data
    })

  useMarketplaceVoiceChildren = (voiceUuid: string) =>
    useQuery<VoiceChild[]>(`${voiceUuid}`, async () => {
      return (await this.getMarketplaceVoiceChildren(voiceUuid)).data
    })

  scaffoldMarketplaceVoice = async (voiceUuid: string) => {
    return await super.getHttpClientInstance().post<{
      clip_path: string
    }>(
      `/marketplace_clip_scaffold.json?voice_uuid=${voiceUuid}`,
      {},
      {
        'X-CSRF-Token': CSRF.getToken(),
        'Content-Type': 'application/json',
      },
    )
  }

  useScaffoldMarketplaceVoice = (voiceUuid: string, onSuccess?: (data: { clip_path: string }) => void | Promise<unknown>, onError?: (error: unknown) => void) => {
    return useMutation<{ clip_path: string }>(
      ['scaffold-marketplace-clip', voiceUuid],
      async () => {
        return (await this.scaffoldMarketplaceVoice(voiceUuid)).data
      },
      {
        onSuccess: async (data) => {
          if (onSuccess) {
            onSuccess(data)
          } else {
            navigateSafely(data.clip_path)
          }
        },
        onError: async (error) => {
          if (onError) {
            onError(error)
          } else {
            Swal.fire('Clip Create Error', 'Something went wrong while creating the clip. Please try again.', 'error')
          }
        },
      },
    )
  }

  // react query function based on scaffoldMarketplaceVoice

  addMarketplaceVoice = (voiceUuid: string, onSuccess?: (data: { status: string }) => void | Promise<unknown>) => {
    return useMutation<{ status: string }>(
      ['marketplace', 'add-voices', voiceUuid],
      async () => {
        return (
          await super.getHttpClientInstance().post<{ status: string }>(
            '/marketplace',
            {
              uuid: voiceUuid,
            },
            {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'X-CSRF-Token': CSRF.getToken(),
            },
          )
        ).data
      },
      {
        onSuccess: async ({ status }) => {
          if (onSuccess) {
            onSuccess({ status })
          }
        },
      },
    )
  }

  useMarketplaceVoices = (
    options: Partial<{ search_text: string; categories: string[]; sort_by: 'popular' | 'new' | 'name' | ''; showAllStyles?: boolean }>,
    queryOptions: Omit<UseQueryOptions<Resemble.MarketplaceVoice[], unknown, Resemble.MarketplaceVoice[], QueryKey>, 'queryKey' | 'queryFn'> | undefined,
  ) =>
    useQuery<Resemble.MarketplaceVoice[]>(
      [options],
      async () => {
        return (
          await super.getHttpClientInstance().post<Resemble.MarketplaceVoice[]>('/marketplace/voices', options, {
            'X-CSRF-Token': CSRF.getToken(),
            'Content-Type': 'application/json',
          })
        ).data
      },
      queryOptions,
    )

  useMarketplaceCategories = () =>
    useQuery<string[]>(['marketplace', 'categories'], async () => {
      return (
        await super.getHttpClientInstance().get<string[]>('/marketplace/categories', {
          'X-CSRF-Token': CSRF.getToken(),
          'Content-Type': 'application/json',
        })
      ).data
    })

  updateVoiceDetails = async (voiceUuid: string, params: UpdateVoiceDetailsParams) => {
    return (
      await super.getHttpClientInstance().post<Voice>(`/voices/${voiceUuid}/update_details`, params, {
        'X-CSRF-Token': CSRF.getToken(),
        'Content-Type': 'application/json',
      })
    ).data
  }

  createSpeakerBlacklistCheck = async (audioData: File, voiceType?: VoiceCloneType) => {
    const formData = new FormData()
    formData.append('data', audioData)
    if (voiceType) {
      formData.append('voice_type', voiceType)
    }

    return new Promise<{ speaker_blacklist_check_uuid: string }>((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhr.open('POST', '/voices/create_speaker_blacklist_check', true)
      xhr.setRequestHeader('X-CSRF-Token', CSRF.getToken())

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText)
            resolve(response)
          } catch (error) {
            reject(new Error('Failed to parse response'))
          }
        } else {
          reject(new Error(`Upload failed: ${xhr.statusText}`))
        }
      }

      xhr.onerror = () => {
        reject(new Error(`Upload error: ${xhr.statusText}`))
      }

      xhr.send(formData)
    })
  }

  getSpeakerBlacklistCheckStatus = async (uuid: string) => {
    return (
      await super.getHttpClientInstance().get<SpeakerBlacklistCheckStatus>(`/voices/get_speaker_blacklist_check/${uuid}`, {
        'X-CSRF-Token': CSRF.getToken(),
      })
    ).data
  }

  getVoiceDescription = async (voiceUuid: string) => {
    return (
      await super.getHttpClientInstance().get<{ description: string }>(`/voices/${voiceUuid}/get_voice_description`, {
        'X-CSRF-Token': CSRF.getToken(),
      })
    ).data
  }

  // Voice Settings Presets API methods
  getVoiceSettingsPresets = async () => {
    return (
      await super.getHttpClientInstance().get<{
        success: boolean
        data: {
          user_presets: VoiceSettingsPreset[]
          default_presets: VoiceSettingsPreset[]
        }
      }>('/api/v2/voice_settings_presets', {
        'X-CSRF-Token': CSRF.getToken(),
        'Content-Type': 'application/json',
      })
    ).data
  }

  createVoiceSettingsPreset = async (preset: { name: string; settings: Record<string, any> }) => {
    // Flatten the structure - send settings directly at top level
    const requestBody = {
      name: preset.name,
      ...preset.settings, // Spread settings directly into the request body
    }

    return (
      await super.getHttpClientInstance().post<{
        success: boolean
        data: VoiceSettingsPreset
        message: string
      }>('/api/v2/voice_settings_presets', requestBody, {
        'X-CSRF-Token': CSRF.getToken(),
        'Content-Type': 'application/json',
      })
    ).data
  }

  updateVoiceSettingsPreset = async (uuid: string, preset: { name?: string; settings: Record<string, any> }) => {
    // Flatten the structure - send settings directly at top level
    const requestBody = {
      name: preset.name,
      ...preset.settings, // Spread settings directly into the request body
    }

    return (
      await super.getHttpClientInstance().patch<{
        success: boolean
        data: VoiceSettingsPreset
        message: string
      }>(`/api/v2/voice_settings_presets/${uuid}`, requestBody, {
        'X-CSRF-Token': CSRF.getToken(),
        'Content-Type': 'application/json',
      })
    ).data
  }

  deleteVoiceSettingsPreset = async (uuid: string) => {
    return (
      await super.getHttpClientInstance().delete(`/api/v2/voice_settings_presets/${uuid}`, {
        'X-CSRF-Token': CSRF.getToken(),
        'Content-Type': 'application/json',
      })
    ).data as {
      success: boolean
      message: string
    }
  }

  getVoiceByUuid = async (uuid: string) => {
    try {
      const response = (
        await super.getHttpClientInstance().get<{ success: boolean; item: any }>(`/api/v2/voices/${uuid}`, {
          'X-CSRF-Token': CSRF.getToken(),
          'Content-Type': 'application/json',
        })
      ).data

      return response.item
    } catch (err) {
      console.warn('Failed to get voice by UUID', err)
    }

    return null
  }

  replaceDataset = async (voiceUuid: string, dataFile: File) => {
    const formData = new FormData()
    formData.append('data', dataFile)

    return (
      await super
        .getHttpClientInstance()
        .configure(API_BASE_URL, 60 * 60 * 1000)
        .post(`/voices/${voiceUuid}/replace_dataset`, formData, {
          'X-CSRF-Token': CSRF.getToken(),
          'Content-Type': 'multipart/form-data',
        })
    ).data
  }
}
